<template>
    <section>
        <div class="container">
            <div class="row admin-cards-row">
                <div class="col-lg-7 mb-4 admin-card-col">
                    <div class="card card-container admin-card">
                        <div class="card-header text-center">
                            <h4>Speicherverbrauch</h4>
                        </div>
                        <div class="card-body">
                            <div v-if="loading" class="text-muted">Lade …</div>
                            <div v-else-if="stats">
                                <div class="stat-row">
                                    <span class="stat-label">Protokoll-Bilder</span>
                                    <span class="stat-value">{{ formatBytes(stats.protokolImages.bytes) }}</span>
                                </div>
                                <p class="stat-detail">{{ stats.protokolImages.fileCount }} Dateien</p>

                                <div class="stat-row">
                                    <span class="stat-label">Reports-Storage</span>
                                    <span class="stat-value">{{ formatBytes(stats.reportsStorage.bytes) }}</span>
                                </div>
                                <p class="stat-detail">
                                    PDF-Hintergrund: {{ formatBytes(stats.reportsStorage.pdfBackgroundBytes) }},
                                    temporäre PDFs: {{ formatBytes(stats.reportsStorage.tempPdfBytes) }}
                                </p>

                                <div class="stat-row">
                                    <span class="stat-label">MongoDB</span>
                                    <span class="stat-value">{{ formatBytes(stats.mongo.totalSize) }}</span>
                                </div>
                                <p class="stat-detail">
                                    Daten: {{ formatBytes(stats.mongo.dataSize) }},
                                    Indizes: {{ formatBytes(stats.mongo.indexSize) }}
                                </p>

                                <div class="stat-row">
                                    <span class="stat-label">Protokolle gesamt</span>
                                    <span class="stat-value">{{ stats.protokolCount }}</span>
                                </div>

                                <div class="stat-row">
                                    <span class="stat-label">Festplatte verfügbar</span>
                                    <span class="stat-value">{{ formatBytes(stats.disk?.freeBytes) }}</span>
                                </div>
                                <p class="stat-detail">
                                    von {{ formatBytes(stats.disk?.totalBytes) }} gesamt
                                    (Dateisystem der Bild-Volumes)
                                </p>
                            </div>
                            <button class="btn btn-outline-primary btn-sm mt-3" @click="loadStats" :disabled="loading">
                                Aktualisieren
                            </button>
                        </div>
                    </div>
                </div>

                <div class="col-lg-5 mb-4 admin-card-col">
                    <div class="card card-container admin-card">
                        <div class="card-header text-center">
                            <h4>Aufbewahrungsrichtlinie</h4>
                        </div>
                        <div class="card-body">
                            <p class="policy-hint">
                                Protokolle und zugehörige Bilder werden gelöscht, wenn seit dem Erstellungsdatum
                                mindestens die festgelegte Anzahl <strong>voller Kalendertage</strong> vergangen ist
                                (heute erstellt = Tag 0, bei 1 Tag frühestens ab morgen).
                                <strong>0</strong> = deaktiviert.
                            </p>
                            <p class="policy-hint policy-excluded">
                                Protokolle mit Status <strong>„In Bearbeitung“</strong> werden bewusst
                                von jeder Bereinigung ausgenommen — auch wenn sie älter als die
                                Aufbewahrungsfrist sind (Schutz laufender Bearbeitung).
                            </p>
                            <div class="form-group retention-field">
                                <label for="retentionDays">Aufbewahrung in Tagen</label>
                                <input
                                    id="retentionDays"
                                    v-model.number="retentionDays"
                                    type="number"
                                    min="0"
                                    max="3650"
                                    class="form-control"
                                />
                            </div>
                            <button
                                class="btn btn-primary btn-block save-policy-btn"
                                @click="saveRetentionPolicy"
                                :disabled="savingPolicy"
                            >
                                Richtlinie speichern
                            </button>

                            <div v-if="stats" class="policy-status mt-4">
                                <p>
                                    Status:
                                    <span v-if="stats.retentionPolicy.enabled">
                                        aktiv ({{ stats.retentionPolicy.retentionDays }} Tage)
                                    </span>
                                    <span v-else>deaktiviert</span>
                                </p>
                                <p v-if="stats.retentionPolicy.eligibleForDeletion > 0">
                                    {{ stats.retentionPolicy.eligibleForDeletion }} Protokoll(e) würden bei der nächsten Bereinigung gelöscht.
                                </p>
                                <p v-if="stats.retentionPolicy.lastCleanupAt">
                                    Letzte Bereinigung: {{ formatDateTime(stats.retentionPolicy.lastCleanupAt) }}
                                    ({{ stats.retentionPolicy.lastCleanupDeletedCount }} gelöscht)
                                </p>
                                <p v-else>Noch keine automatische Bereinigung ausgeführt.</p>
                            </div>

                            <button
                                class="btn btn-outline-danger btn-sm mt-2"
                                @click="runCleanupNow"
                                :disabled="cleanupRunning || !stats?.retentionPolicy.enabled"
                            >
                                Bereinigung jetzt ausführen
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-12 mb-4">
                    <div class="card card-container admin-card">
                        <div class="card-header text-center">
                            <h4>App-Mindestversion</h4>
                        </div>
                        <div class="card-body">
                            <p class="policy-hint">
                                Leere Mindestversion = Prüfung aus. Die Sperre gilt nur für die native App,
                                nicht für das Web-Portal. Eine zu hoch gesetzte Version sperrt alle Geräte
                                im Feld — bitte die Verteilung unten prüfen.
                            </p>
                            <div v-if="compatLoading" class="text-muted">Lade …</div>
                            <div v-else class="compat-grid">
                                <div class="compat-platform">
                                    <h5>iOS</h5>
                                    <label for="iosMinVersion">Mindestversion</label>
                                    <input
                                        id="iosMinVersion"
                                        v-model="compatForm.ios.minVersion"
                                        type="text"
                                        class="form-control"
                                        placeholder="z. B. 1.2.0 — leer = aus"
                                    />
                                    <label for="iosStoreUrl">Store-URL</label>
                                    <input
                                        id="iosStoreUrl"
                                        v-model="compatForm.ios.storeUrl"
                                        type="url"
                                        class="form-control"
                                        placeholder="https://apps.apple.com/…"
                                    />
                                    <p class="stat-detail">
                                        Aktiv (30 Tage): {{ compatUsage.ios.activeUsers }} Nutzer
                                    </p>
                                    <ul v-if="compatUsage.ios.versions.length" class="compat-versions">
                                        <li v-for="row in compatUsage.ios.versions" :key="'ios-' + row.name">
                                            {{ row.name || 'unbekannt' }} — {{ row.count }}
                                        </li>
                                    </ul>
                                    <p v-else class="stat-detail">Noch keine iOS-App-Nutzung erfasst.</p>
                                    <p
                                        v-if="iosWouldBlock > 0"
                                        class="compat-warn"
                                    >
                                        Würde {{ iosWouldBlock }} von {{ compatUsage.ios.activeUsers }}
                                        aktiven iOS-Nutzern aussperren.
                                    </p>
                                </div>
                                <div class="compat-platform">
                                    <h5>Android</h5>
                                    <label for="androidMinVersion">Mindestversion</label>
                                    <input
                                        id="androidMinVersion"
                                        v-model="compatForm.android.minVersion"
                                        type="text"
                                        class="form-control"
                                        placeholder="z. B. 1.2.0 — leer = aus"
                                    />
                                    <label for="androidStoreUrl">Store-URL</label>
                                    <input
                                        id="androidStoreUrl"
                                        v-model="compatForm.android.storeUrl"
                                        type="url"
                                        class="form-control"
                                        placeholder="https://play.google.com/…"
                                    />
                                    <p class="stat-detail">
                                        Aktiv (30 Tage): {{ compatUsage.android.activeUsers }} Nutzer
                                    </p>
                                    <ul v-if="compatUsage.android.versions.length" class="compat-versions">
                                        <li v-for="row in compatUsage.android.versions" :key="'and-' + row.name">
                                            {{ row.name || 'unbekannt' }} — {{ row.count }}
                                        </li>
                                    </ul>
                                    <p v-else class="stat-detail">Noch keine Android-App-Nutzung erfasst.</p>
                                    <p
                                        v-if="androidWouldBlock > 0"
                                        class="compat-warn"
                                    >
                                        Würde {{ androidWouldBlock }} von {{ compatUsage.android.activeUsers }}
                                        aktiven Android-Nutzern aussperren.
                                    </p>
                                </div>
                            </div>
                            <div v-if="!compatLoading" class="form-group retention-field mt-3">
                                <label for="webAppUrl">Web-App-URL (Ausweg bei Sperre)</label>
                                <input
                                    id="webAppUrl"
                                    v-model="compatForm.webAppUrl"
                                    type="url"
                                    class="form-control"
                                />
                                <label for="blockMessage">Hinweistext (optional)</label>
                                <textarea
                                    id="blockMessage"
                                    v-model="compatForm.blockMessage"
                                    class="form-control"
                                    rows="3"
                                    maxlength="500"
                                    placeholder="Leer = Standardtext in der App"
                                />
                            </div>
                            <button
                                class="btn btn-primary btn-block save-policy-btn"
                                @click="saveCompatibility"
                                :disabled="compatLoading || savingCompat"
                            >
                                Mindestversionen speichern
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <confirm-dialogue ref="confirmDialogue" />
    </section>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import API_BACKEND_URL from '@/API_URL/api_back_end.js';
import { getToken } from '@/services/tokenStore';
import ConfirmDialogue from './static_elements/ConfirmDialogue.vue';
import { countWouldBlock } from '@/utils/appVersion';

export default {
    name: 'AdminSystem',
    components: { ConfirmDialogue },
    data() {
        return {
            toast: useToast(),
            loading: false,
            savingPolicy: false,
            cleanupRunning: false,
            stats: null,
            retentionDays: 0,
            compatLoading: false,
            savingCompat: false,
            compatForm: {
                ios: { minVersion: '', storeUrl: '' },
                android: { minVersion: '', storeUrl: '' },
                webAppUrl: 'https://fma.otfm.at/',
                blockMessage: '',
            },
            compatUsage: {
                ios: { versions: [], activeUsers: 0 },
                android: { versions: [], activeUsers: 0 },
            },
        };
    },
    computed: {
        iosWouldBlock() {
            return countWouldBlock(
                this.compatUsage.ios.versions,
                this.compatForm.ios.minVersion,
            );
        },
        androidWouldBlock() {
            return countWouldBlock(
                this.compatUsage.android.versions,
                this.compatForm.android.minVersion,
            );
        },
    },
    methods: {
        authHeaders() {
            const token = getToken();
            return token ? { token } : {};
        },
        formatBytes(bytes) {
            if (!bytes || bytes <= 0) {
                return '0 B';
            }
            const units = ['B', 'KB', 'MB', 'GB', 'TB'];
            const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
            const value = bytes / (1024 ** index);
            return `${value.toFixed(index === 0 ? 0 : 2)} ${units[index]}`;
        },
        formatDateTime(value) {
            if (!value) {
                return '';
            }
            return new Date(value).toLocaleString('de-AT');
        },
        async loadStats() {
            this.loading = true;
            try {
                const res = await axios.get(`${API_BACKEND_URL}admin/storage-stats`, {
                    headers: this.authHeaders(),
                });
                this.stats = res.data;
                this.retentionDays = res.data.retentionPolicy.retentionDays;
            } catch (err) {
                const status = err.response?.status;
                if (status === 401 || status === 403) {
                    this.toast.warning('Keine Berechtigung für diesen Bereich.', { timeout: 3000 });
                    this.$router.push('/home');
                } else {
                    this.toast.warning('Speicherdaten konnten nicht geladen werden.', { timeout: 3000 });
                }
            } finally {
                this.loading = false;
            }
        },
        async saveRetentionPolicy() {
            if (this.retentionDays < 0 || this.retentionDays > 3650) {
                this.toast.warning('Bitte einen Wert zwischen 0 und 3650 eingeben.', { timeout: 3000 });
                return;
            }

            this.savingPolicy = true;
            try {
                const res = await axios.put(
                    `${API_BACKEND_URL}admin/retention-policy`,
                    { retentionDays: this.retentionDays },
                    { headers: this.authHeaders() },
                );
                const cleanup = res.data.cleanup;
                if (cleanup && !cleanup.skipped) {
                    this.toast.success(
                        `Richtlinie gespeichert. ${cleanup.deleted} Protokoll(e) gelöscht.`,
                        { timeout: 3000 },
                    );
                } else {
                    this.toast.success('Aufbewahrungsrichtlinie gespeichert.', { timeout: 2000 });
                }
                await this.loadStats();
            } catch (err) {
                this.toast.warning('Richtlinie konnte nicht gespeichert werden.', { timeout: 3000 });
                console.error(err);
            } finally {
                this.savingPolicy = false;
            }
        },
        async runCleanupNow() {
            this.cleanupRunning = true;
            try {
                const res = await axios.post(
                    `${API_BACKEND_URL}admin/retention-cleanup`,
                    {},
                    { headers: this.authHeaders() },
                );
                if (res.data.skipped) {
                    this.toast.info('Aufbewahrung ist deaktiviert (0 Tage).', { timeout: 3000 });
                } else {
                    this.toast.success(`${res.data.deleted} Protokoll(e) gelöscht.`, { timeout: 3000 });
                }
                await this.loadStats();
            } catch (err) {
                this.toast.warning('Bereinigung fehlgeschlagen.', { timeout: 3000 });
                console.error(err);
            } finally {
                this.cleanupRunning = false;
            }
        },
        applyCompatibilityPayload(data) {
            this.compatForm = {
                ios: {
                    minVersion: data.ios?.minVersion || '',
                    storeUrl: data.ios?.storeUrl || '',
                },
                android: {
                    minVersion: data.android?.minVersion || '',
                    storeUrl: data.android?.storeUrl || '',
                },
                webAppUrl: data.webAppUrl || 'https://fma.otfm.at/',
                blockMessage: data.blockMessage || '',
            };
            this.compatUsage = {
                ios: {
                    versions: data.usage?.ios?.versions || [],
                    activeUsers: data.usage?.ios?.activeUsers || 0,
                },
                android: {
                    versions: data.usage?.android?.versions || [],
                    activeUsers: data.usage?.android?.activeUsers || 0,
                },
            };
        },
        async loadCompatibility() {
            this.compatLoading = true;
            try {
                const res = await axios.get(`${API_BACKEND_URL}admin/app-compatibility`, {
                    headers: this.authHeaders(),
                });
                this.applyCompatibilityPayload(res.data);
            } catch (err) {
                const status = err.response?.status;
                if (status !== 401 && status !== 403) {
                    this.toast.warning('App-Versionen konnten nicht geladen werden.', { timeout: 3000 });
                }
            } finally {
                this.compatLoading = false;
            }
        },
        async saveCompatibility() {
            const blocked = this.iosWouldBlock + this.androidWouldBlock;
            const total = this.compatUsage.ios.activeUsers + this.compatUsage.android.activeUsers;
            let message = 'Mindestversionen wirklich speichern? Die Sperre gilt sofort für die nächste Prüfung in der App.';
            if (blocked > 0) {
                message = `Das würde ${blocked} von ${total} aktiven App-Nutzern (30 Tage) aussperren. Wirklich speichern?`;
            }
            const ok = await this.$refs.confirmDialogue.show({
                title: 'App-Mindestversion',
                message,
                okButton: 'Speichern',
                cancelButton: 'Abbrechen',
            });
            if (!ok) {
                return;
            }
            this.savingCompat = true;
            try {
                const res = await axios.put(
                    `${API_BACKEND_URL}admin/app-compatibility`,
                    this.compatForm,
                    { headers: this.authHeaders() },
                );
                this.applyCompatibilityPayload(res.data);
                this.toast.success('App-Mindestversionen gespeichert.', { timeout: 2000 });
            } catch (err) {
                const apiMessage = err.response?.data?.err_message;
                this.toast.warning(apiMessage || 'Mindestversionen konnten nicht gespeichert werden.', {
                    timeout: 3000,
                });
                console.error(err);
            } finally {
                this.savingCompat = false;
            }
        },
    },
    created() {
        if (this.$store.state.auth.user?.role !== 'administrator') {
            this.$router.push('/home');
            return;
        }
        this.loadStats();
        this.loadCompatibility();
    },
};
</script>

<style scoped>
section {
    margin-top: var(--app-header-height);
    padding: 0.85rem 1rem calc(var(--app-footer-height) + 0.5rem);
}

.card-container {
    padding: 1.25rem;
    border-radius: 15px;
    border: 2px solid #c1c1c1;
    background-color: #fff;
}

.admin-cards-row {
    align-items: stretch;
}

.admin-card-col {
    display: flex;
}

.admin-card {
    width: 100%;
    display: flex;
    flex-direction: column;
}

.admin-card .card-body {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.card-header h4 {
    margin-bottom: 0;
}

.retention-field {
    margin-bottom: 1.5rem;
}

.save-policy-btn {
    margin-top: 0.25rem;
}

.stat-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-top: 1rem;
    font-size: 1.05rem;
}

.stat-label {
    font-weight: 600;
}

.stat-value {
    font-family: monospace;
}

.stat-detail {
    margin: 0.2rem 0 0;
    color: #666;
    font-size: 0.9rem;
}

.policy-hint {
    color: #555;
    font-size: 0.95rem;
}

.policy-excluded {
    margin-top: 0.75rem;
    padding: 0.65rem 0.75rem;
    background-color: #f8f9fa;
    border-radius: 8px;
    border-left: 3px solid #264271;
}

.policy-status p {
    margin-bottom: 0.4rem;
    font-size: 0.95rem;
}

.compat-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.25rem;
}

@media (min-width: 768px) {
    .compat-grid {
        grid-template-columns: 1fr 1fr;
    }
}

.compat-platform h5 {
    margin: 0 0 0.65rem;
    font-size: 1.05rem;
}

.compat-platform label {
    display: block;
    margin-top: 0.65rem;
    margin-bottom: 0.25rem;
    font-weight: 600;
    font-size: 0.9rem;
}

.compat-versions {
    margin: 0.35rem 0 0;
    padding-left: 1.1rem;
    font-size: 0.9rem;
    color: #444;
}

.compat-warn {
    margin-top: 0.6rem;
    padding: 0.55rem 0.7rem;
    background: #fff3cd;
    border-radius: 8px;
    border-left: 3px solid #d39e00;
    color: #664d03;
    font-size: 0.9rem;
}
</style>
